
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Award, ChevronLeft, ChevronRight, Users, TrendingUp, Settings } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { NavItem, getNavigationItems } from "./navigation/NavigationItems";
import { NavigationSettings } from "./navigation/NavigationSettings";
import { SearchDialog } from "./search/SearchDialog";
import { Badge } from "./ui/badge";
import { MobileNavButton } from "./navigation/MobileNavButton";
import { MobileNavBar } from "./navigation/MobileNavBar";
import { Button } from "./ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

interface NavigationProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const Navigation = ({ isOpen, setIsOpen }: NavigationProps) => {
  const location = useLocation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'dark'
    }
    return 'dark'
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setIsOpen(true);
      else setIsOpen(false);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [setIsOpen]);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Don't render navigation on the landing page (root route)
  if (location.pathname === '/') {
    return null;
  }

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const handleMobileClose = () => {
    if (isMobile) setIsOpen(false);
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Filter navigation items for mobile
  const filteredNavigationItems = getNavigationItems(handleMobileClose).filter(item => {
    // First, filter out items that require authentication
    if (!user && !item.publicAccess) {
      return false;
    }
    
    // For mobile, only show Community, Trending, and Settings
    if (isMobile) {
      return ['Community', 'Trending', 'Settings'].includes(item.label);
    }
    
    // For desktop, show all items
    return true;
  });

  // Mobile only navigation items
  const mobileOnlyItems = isMobile ? [
    {
      icon: Users,
      label: "Community",
      path: "/communities",
      onClick: handleMobileClose
    },
    {
      icon: TrendingUp,
      label: "Trending",
      path: "/trending",
      onClick: handleMobileClose
    },
    {
      icon: Settings,
      label: "Settings",
      path: "/settings",
      onClick: handleMobileClose
    }
  ] : [];

  return (
    <>
      <nav className={`fixed top-0 left-0 h-screen bg-background/95 backdrop-blur-xl border-r border-border
                      transform transition-all duration-300 ease-in-out z-50 lg:translate-x-0
                      ${!isOpen ? '-translate-x-full' : 'translate-x-0'}
                      ${isCollapsed ? 'w-16' : 'w-64'}`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-6">
            {!isCollapsed && (
              <Link to="/" className="flex items-center gap-2" onClick={handleMobileClose}>
                <h1 className="text-xl font-bold text-foreground flex items-center font-['SF_Pro_Display']">
                  <span className="text-foreground font-bold">Flux</span>
                  <span className="text-foreground font-bold">Void</span>
                </h1>
                {user && (
                  <Badge 
                    variant="pioneer"
                    className="flex items-center gap-1.5 px-3 py-1 text-sm bg-background animate-shine bg-[length:400%_100%] bg-gradient-to-r from-background via-muted to-background shadow-lg"
                  >
                    <Award className="w-3.5 h-3.5" />
                    Pioneer
                  </Badge>
                )}
              </Link>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto"
              onClick={toggleCollapse}
            >
              {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>

          {!isCollapsed && user && (
            <div className="mb-4">
              <SearchDialog />
            </div>
          )}
          
          <div className="space-y-2">
            {isMobile ? (
              // Mobile view - only show the three specific items
              mobileOnlyItems.map((item) => (
                <NavItem
                  key={item.path}
                  icon={item.icon}
                  label={item.label}
                  path={item.path}
                  onClick={item.onClick}
                  isCollapsed={isCollapsed}
                />
              ))
            ) : (
              // Desktop view - show all filtered items
              user ? (
                filteredNavigationItems.map((item) => (
                  <NavItem
                    key={item.path}
                    {...item}
                    onClick={handleMobileClose}
                    isCollapsed={isCollapsed}
                  />
                ))
              ) : (
                <>
                  {filteredNavigationItems.map((item) => (
                    <NavItem
                      key={item.path}
                      {...item}
                      onClick={handleMobileClose}
                      isCollapsed={isCollapsed}
                    />
                  ))}
                  {!isCollapsed && (
                    <Link
                      to="/signup"
                      className="w-full justify-start text-foreground hover:bg-muted hover:text-foreground font-['SF_Pro_Display'] flex items-center space-x-2 p-2 rounded-lg transition-all duration-200"
                      onClick={handleMobileClose}
                    >
                      Sign-up
                    </Link>
                  )}
                </>
              )
            )}
          </div>
        </div>
      </nav>

      <MobileNavBar user={user} />
      <MobileNavButton 
        isOpen={isOpen} 
        setIsOpen={setIsOpen} 
        showOnInbox={location.pathname !== '/inbox'} 
      />
    </>
  );
};

export default Navigation;
